import { Avatar, AvatarFallback, AvatarImage } from '@ophelos/ui/components/avatar';
import { Badge } from '@ophelos/ui/components/badge';
import { BadgePercent, CalendarClock, ChevronRight, Info, Loader2 } from 'lucide-react';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useTranslation } from '@/hooks/useTranslation';
import { getCreditorInitials } from '@/lib/creditor';
import { formatDisplayField } from '@/lib/purchase-date';
import type { DisplayField } from '@/types/core-api';
import { isClearedDebt } from '@/lib/debtFilters';
import { debtHeadline } from '@/lib/debtHeadline';
import { scheduledPaymentDate } from '@/lib/paymentPlans';
import type { DebtData } from '@/types';
import { CurrencyDisplay } from '../ui/CurrencyDisplay';
import { PartialPaymentProgress } from '../ui/PartialPaymentProgress';
import { PaymentPlanProgress } from '../ui/PaymentPlanProgress';

interface DebtButtonProps {
  debt: DebtData;
  onClick: (debt: DebtData) => void;
  variant?: 'default' | 'compact';
  pending?: boolean;
}


/** Joined summary. A date is included only after a text field, so a bare date never reads as a due date. */
function displaySummary(fields: DisplayField[] | undefined, language: string): string {
  const parts: string[] = [];
  let seenText = false;
  for (const field of fields ?? []) {
    if (field.format === 'date' && !seenText) continue;
    if (field.format === 'text') seenText = true;
    parts.push(formatDisplayField(field, language));
  }
  return parts.join(' · ');
}

function InfoTooltip({ message }: { message: string }) {
  return (
    <div className="relative inline-flex group">
      <div className="size-5 rounded-full bg-muted text-muted-foreground text-xs flex items-center justify-center shrink-0">
        <Info className="size-3" />
      </div>
      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 hidden group-hover:block bg-foreground text-background text-xs rounded-md p-2 shadow-lg z-50 text-center pointer-events-none">
        {message}
      </div>
    </div>
  );
}

export function DebtButton({ debt, onClick, variant = 'default', pending = false }: DebtButtonProps) {
  const { t, currentLanguage } = useTranslation();
  const { activeOffers } = useDebtData();
  const initials = getCreditorInitials(debt.originalCreditor);
  // Same predicate the Dashboard groups on, so a card never advertises an offer under a
  // heading that already calls it cleared.
  const isCompleted = isClearedDebt(debt);
  const isInProgress = Boolean(debt.paymentPlan);
  const isNegativeBalance = debt.currentBalance < 0;
  const purchase = displaySummary(debt.displayFields, currentLanguage);
  const scheduledDate = scheduledPaymentDate(debt.paymentPlan);
  // Balance, not the PAYING node: the ledger credits ~15s before the journey event moves the
  // node, so node-gating left a part-paid debt with no progress until the customer reloaded.
  const isPartPaid = debt.currentBalance > 0 && debt.originalAmount > debt.currentBalance;
  // The instalment the customer will pay next; the first one when nothing has cleared yet.
  const schedule = debt.paymentPlan?.fullPlan?.schedule;
  const nextInstalment = schedule?.find((item) => item.status !== 'succeeded') ?? schedule?.[0];
  const instalmentAmount = nextInstalment ? nextInstalment.subunits / 100 : undefined;

  // The live offer's best discount and the price it gives, shared with the chat's debt button.
  const { bestPct, amount: headlineAmount, discounted: showBeforeAfter } = debtHeadline(debt, activeOffers[debt.id]);
  const showDiscountBadge = bestPct != null;
  const discountLabel = bestPct != null ? t('discount.upToPercentOff', { pct: Math.round(bestPct * 100) }) : null;

  if (variant === 'compact') {
    return (
      <button
        onClick={() => !isNegativeBalance && onClick(debt)}
        disabled={isNegativeBalance}
        className={`w-full p-4 rounded-2xl border border-transparent transition group text-left ${isNegativeBalance ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-accent/50 hover:border-ring/50'}`}
      >
        <div className="flex items-center gap-3">
          <Avatar className="size-9">
            <AvatarImage src={debt.creditorLogoUrl} alt={debt.originalCreditor} />
            <AvatarFallback className="text-sm font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-base font-medium">{debt.originalCreditor}</p>
            {purchase && <p className="text-xs text-muted-foreground mt-0.5 wrap-anywhere">{purchase}</p>}
            <p className="text-xs text-muted-foreground mt-0.5">{debt.accountNumber}</p>
          </div>
          <div className="font-semibold shrink-0 flex flex-col items-end leading-tight">
            {showBeforeAfter && (
              <CurrencyDisplay amount={debt.currentBalance} currency={debt.currency} className="text-xs text-muted-foreground line-through font-normal" />
            )}
            <CurrencyDisplay amount={headlineAmount} currency={debt.currency} />
          </div>
          {isNegativeBalance ? (
            <InfoTooltip message={t('debt.contactSupport')} />
          ) : (
            <ChevronRight className="size-4 text-muted-foreground group-hover:text-primary/75 group-hover:translate-x-1 transition" />
          )}
        </div>
      </button>
    );
  }

  const isDisabled = isNegativeBalance || pending;

  return (
    <button
      className={`relative w-full p-4 rounded-2xl border border-transparent transition duration-300 group ${pending ? 'opacity-60 cursor-wait' : isNegativeBalance ? 'opacity-50 cursor-not-allowed' : isCompleted ? 'cursor-pointer hover:bg-muted opacity-80' : 'cursor-pointer hover:bg-accent/50 hover:border-ring/50'}`}
      onClick={() => !isDisabled && onClick(debt)}
      disabled={isDisabled}
    >
      <div className="flex flex-col gap-3 w-full">
        <div className="relative flex max-xs:flex-col xs:items-center xs:justify-between w-full gap-4">
          <div className="max-xs:flex max-xs:flex-col xs:grid xs:grid-cols-16 items-center gap-4 w-full">
            <Avatar className="size-9 xs:col-span-2 max-xs:mx-auto">
              <AvatarImage src={debt.creditorLogoUrl} alt={debt.originalCreditor} />
              <AvatarFallback
                className={`text-sm font-semibold ${isCompleted ? 'bg-muted text-muted-foreground' : 'bg-accent text-accent-foreground'}`}
              >
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0 xs:text-left break-all flex flex-col xs:items-start xs:col-span-10 overflow-hidden">
              <h3 className={`font-medium text-xl xs:text-base ${isCompleted ? 'text-muted-foreground' : ''}`}>{debt.originalCreditor}</h3>

              {purchase && <p className="text-sm text-muted-foreground mt-0.5">{purchase}</p>}
              {debt.description && <p className="text-sm text-muted-foreground mt-0.5">{debt.description}</p>}
              <p className="text-sm text-muted-foreground mt-0.5">{debt.accountNumber}</p>
              {showDiscountBadge && (
                <Badge variant="warning" shape="square" className="mt-1.5 gap-1 max-xs:mx-auto max-w-full whitespace-normal text-left leading-tight">
                  <BadgePercent className="size-3 shrink-0" />
                  <span className="min-w-0">{discountLabel}</span>
                </Badge>
              )}
              {scheduledDate && (
                <Badge variant="info" shape="square" className="mt-1.5 gap-1 max-xs:mx-auto max-w-full whitespace-normal text-left leading-tight">
                  <CalendarClock className="size-3 shrink-0" />
                  <span className="min-w-0">{t('dashboard.scheduledFor', { date: new Date(`${scheduledDate}T00:00:00`).toLocaleDateString() })}</span>
                </Badge>
              )}
            </div>
            <div className="flex items-center justify-center xs:justify-end gap-3 shrink-0 max-xs:self-center xs:col-span-4">
              <div className="text-right">
                <div className="flex flex-col items-center xs:items-end leading-tight">
                  <CurrencyDisplay
                    amount={headlineAmount}
                    currency={debt.currency}
                    className={`text-2xl xs:text-lg font-semibold tracking-tight ${isCompleted ? 'text-muted-foreground' : ''}`}
                  />
                  {showBeforeAfter && (
                    <CurrencyDisplay amount={debt.currentBalance} currency={debt.currency} className="text-sm text-muted-foreground line-through font-normal" />
                  )}
                  {(debt.paymentPlan || isPartPaid) && !scheduledDate && (
                    <span className="text-xs text-muted-foreground">{t('paymentPlan.remainingBalance')}</span>
                  )}
                </div>
              </div>
              {pending ? (
                <Loader2 className="size-4 text-muted-foreground animate-spin" />
              ) : isNegativeBalance ? (
                <InfoTooltip message={t('debt.contactSupport')} />
              ) : null}
            </div>
          </div>
          {!isDisabled && (
            <div className="max-xs:absolute max-xs:right-0 max-xs:top-1/2 max-xs:-translate-y-1/2 max-xs:p-1 max-xs:rounded-full max-xs:border shrink-0 text-muted-foreground group-hover:text-primary/75 group-hover:translate-x-1 transition">
              <ChevronRight className="size-4" />
            </div>
          )}
        </div>
        {isInProgress && !scheduledDate && debt.paymentPlan && debt.paymentPlan.totalPayments && debt.paymentPlan.completedPayments !== undefined && (
          <PaymentPlanProgress
            totalPayments={debt.paymentPlan.totalPayments}
            completedPayments={debt.paymentPlan.completedPayments}
            originalAmount={debt.originalAmount}
            amountPaid={debt.paymentBreakdown.total}
            instalmentAmount={instalmentAmount}
            currency={debt.currency}
          />
        )}
        {/* Held back while pending: a payment mid-flight would show a part-settled amount under the spinner.
            A plan already has its instalment bar above, so the paid-amount bar is for lump-sum debts only. */}
        {!pending && !scheduledDate && !debt.paymentPlan && isPartPaid && (
          <PartialPaymentProgress originalAmount={debt.originalAmount} currentBalance={debt.currentBalance} currency={debt.currency} />
        )}
      </div>
    </button>
  );
}
